/**
 * Image cache utility for tracking loaded images
 * Helps ProgressiveImage component reuse already loaded images
 */

interface ImageCacheEntry {
	loaded: boolean;
	error: boolean;
	timestamp: number;
}

class ImageCache {
	private cache: Map<string, ImageCacheEntry> = new Map();
	private readonly maxAge = 5 * 60 * 1000; // 5 minutes

	/**
	 * Check if image is already loaded in browser cache
	 * Returns immediately if image is already cached
	 */
	private checkBrowserCache(src: string): Promise<boolean> {
		return new Promise((resolve) => {
			// First, try to check if image is already in DOM
			const existingImg = document.querySelector(`img[src="${src}"]`) as HTMLImageElement;
			if (existingImg && existingImg.complete && existingImg.naturalWidth > 0) {
				resolve(true);
				return;
			}

			const img = new Image();
			let resolved = false;
			
			// If image is already complete, it's cached
			img.onload = () => {
				if (!resolved) {
					resolved = true;
					resolve(true);
				}
			};
			
			img.onerror = () => {
				if (!resolved) {
					resolved = true;
					resolve(false);
				}
			};
			
			// Set src to trigger load check
			// If already cached, onload fires immediately
			img.src = src;
			
			// If image is already complete (cached), resolve immediately
			if (img.complete && img.naturalWidth > 0) {
				if (!resolved) {
					resolved = true;
					resolve(true);
				}
			}
		});
	}

	/**
	 * Synchronously check if image might be cached (fast check)
	 */
	isLikelyCached(src: string): boolean {
		// Check our cache first
		const entry = this.getEntry(src);
		if (entry?.loaded) {
			return true;
		}

		// Check if image exists in DOM
		const existingImg = document.querySelector(`img[src="${src}"]`) as HTMLImageElement;
		if (existingImg && existingImg.complete && existingImg.naturalWidth > 0) {
			return true;
		}

		return false;
	}

	/**
	 * Check if image is cached (in our cache or browser cache)
	 */
	async isCached(src: string): Promise<boolean> {
		// Check our cache first
		const entry = this.cache.get(src);
		if (entry) {
			// Check if entry is still valid
			const age = Date.now() - entry.timestamp;
			if (age < this.maxAge) {
				return entry.loaded;
			}
			// Entry expired, remove it
			this.cache.delete(src);
		}

		// Check browser cache
		const browserCached = await this.checkBrowserCache(src);
		if (browserCached) {
			this.setCached(src, true);
		}
		
		return browserCached;
	}

	/**
	 * Mark image as loaded in cache
	 */
	setCached(src: string, loaded: boolean, error: boolean = false): void {
		this.cache.set(src, {
			loaded,
			error,
			timestamp: Date.now(),
		});
	}

	/**
	 * Get cache entry
	 */
	getEntry(src: string): ImageCacheEntry | undefined {
		const entry = this.cache.get(src);
		if (entry) {
			const age = Date.now() - entry.timestamp;
			if (age < this.maxAge) {
				return entry;
			}
			// Entry expired, remove it
			this.cache.delete(src);
		}
		return undefined;
	}

	/**
	 * Clear expired entries
	 */
	clearExpired(): void {
		const now = Date.now();
		for (const [src, entry] of this.cache.entries()) {
			if (now - entry.timestamp > this.maxAge) {
				this.cache.delete(src);
			}
		}
	}

	/**
	 * Clear all cache
	 */
	clear(): void {
		this.cache.clear();
	}
}

// Singleton instance
export const imageCache = new ImageCache();

// Clean up expired entries periodically
if (typeof window !== 'undefined') {
	setInterval(() => {
		imageCache.clearExpired();
	}, 60 * 1000); // Every minute
}

